import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import apiCall from '../../../api';

export interface LogoUploadProps {
  onLogoSelect?: (file: File | null) => void;
  onLogoUploadToAPI?: (logoFile: File) => Promise<boolean>;
  selectedLogo?: File | null;
  disabled?: boolean;
  maxSize?: number; // in MB, default 1MB
  acceptedFormats?: string[]; // default ['jpg', 'jpeg', 'png']
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  onLogoSelect,
  onLogoUploadToAPI,
  selectedLogo = null,
  disabled = false,
  maxSize = 1,
  acceptedFormats = ['jpg', 'jpeg', 'png']
}) => {
  const { t } = useTranslation();
  const user = useSelector((state: any) => state.auth.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch existing uploaded logo on component mount
  useEffect(() => {
    fetchUploadedLogo();

    return () => {
      if (uploadedLogoUrl && uploadedLogoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(uploadedLogoUrl);
      }
    };
  }, [user?.userId]);

  // Update preview when selectedLogo changes
  useEffect(() => {
    if (selectedLogo) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          setLogoPreview(e.target.result as string);
        }
      };
      reader.readAsDataURL(selectedLogo);
    } else {
      setLogoPreview(null);
    }
  }, [selectedLogo]);

  const fetchUploadedLogo = async () => {
    if (!user?.userId) return;

    try {
      setIsLoading(true);
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = localStorage.getItem('token');

      const logoResponse = await apiCall.get(
        `${baseUrl}/signup/invoice-logo-stream?userId=${user.userId}`,
        {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (logoResponse?.status === 200 && logoResponse.data.size > 0) {
        const logoUrl = URL.createObjectURL(logoResponse.data);
        setUploadedLogoUrl(logoUrl);
      }
    } catch (error) {
     } finally {
      setIsLoading(false);
    }
  };

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type — same rule as the create-invoice logo upload.
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!file.type.startsWith('image/') || !acceptedFormats.includes(fileExtension)) {
      toast.error(t('INVALID_LOGO_FORMAT') as string);
      return;
    }

    // Validate file size (max 1 MB, matching create-invoice).
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(t('LOGO_FILE_SIZE_EXCEEDED') as string);
      return;
    }

    // Notify parent component
    onLogoSelect?.(file);

    // Upload to API if handler provided
    if (onLogoUploadToAPI) {
      uploadLogoToAPI(file);
    }
  };

  const uploadLogoToAPI = async (logoFile: File) => {
    try {
      setIsLoading(true);
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('file', logoFile);

      const response = await apiCall.post(
        `${baseUrl}/signup/invoice-logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        toast.success('Logo uploaded successfully');
        
        // Refresh uploaded logo
        await fetchUploadedLogo();
        return true;
      } else {
        toast.error('Failed to upload logo');
        return false;
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to upload logo';
      console.error('Logo upload error:', error);
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    onLogoSelect?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteUploadedLogo = async () => {
    try {
      setIsLoading(true);
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = localStorage.getItem('token');

      const result = await apiCall.delete(
        `${baseUrl}/signup/user-logo?userId=${user.userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (result?.status === 200) {
        toast.success('Logo deleted successfully');
        setUploadedLogoUrl(null);
        if (uploadedLogoUrl) {
          URL.revokeObjectURL(uploadedLogoUrl);
        }
      } else {
        toast.error('Failed to delete logo');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to delete logo';
      console.error('Logo deletion error:', error);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="logo-upload-container">
      {/* Display current uploaded logo */}
      {uploadedLogoUrl && !logoPreview && (
        <div >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src={uploadedLogoUrl}
              alt="Current logo"
              style={{ maxWidth: '100px', maxHeight: '60px', objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={handleDeleteUploadedLogo}
              disabled={isLoading || disabled}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#FF5252',
                border: 'none',
                color: 'white',
                cursor: isLoading || disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                padding: '0',
                opacity: isLoading || disabled ? 0.6 : 1
              }}
              title="Delete logo"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Display newly selected logo preview */}
      {logoPreview && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative', display: 'flex' , justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={logoPreview}
              alt="New logo preview"
              style={{ maxHeight: '200px', maxWidth: '500px', border: '1.5px solid #e5e7eb', objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={handleRemoveLogo}
              disabled={isLoading || disabled}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#FF5252',
                border: 'none',
                color: 'white',
                cursor: isLoading || disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                padding: '0',
                opacity: isLoading || disabled ? 0.6 : 1
              }}
              title="Remove logo"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div style={{ marginBottom: '16px', fontSize: '12px', color: '#68727D' }}>
          Loading logo...
        </div>
      )}

      {/* File input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.map(fmt => `.${fmt}`).join(',')}
        onChange={handleLogoSelect}
        disabled={disabled || isLoading}
        style={{ display: 'none' }}
      />

      {/* Upload button */}
      <label
        htmlFor="logo-upload"
        style={{
          display: 'flex',
          height: '40px',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px 16px',
          borderRadius: '8px',
          border: '1px dashed #D8D5EA',
          cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
          backgroundColor: disabled || isLoading ? '#f5f5f5' : '#fafafa',
          opacity: disabled || isLoading ? 0.6 : 1,
          transition: 'all 0.2s'
        }}
        onClick={() => !disabled && !isLoading && fileInputRef.current?.click()}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M3 13L3 15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17L15 17C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15L17 13M13 6L10 3M10 3L7 6M10 3L10 13"
            stroke="#6750B8"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span style={{ fontSize: '14px', color: '#6750B8', fontWeight: '500' }}>
          {selectedLogo ? selectedLogo.name : t('UPLOAD_YOUR_LOGO') as string}
        </span>
      </label>
    </div>
  );
};
